// src/components/open-houses/checkin/KioskSetup.jsx
// Agent-facing setup screen shown before guest mode: confirm who's hosting
// (required — every sign-in becomes that agent's lead), pick a question
// template, and set a 4-digit exit PIN.
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_TEMPLATE, BUILT_IN_TEMPLATES } from "../../../utils/checkinFlow";
import { listenCheckinTemplates, getUsers } from "../../../utils/firebasefunctions";
import { useStateValue } from "../../../context/StateProvider";
import {
  eligibleHostingAgents,
  hostDisplayName,
  openHouseHasHost,
} from "../../../utils/openHouseHosts";

export default function KioskSetup({ user, openHouse, onStart, onBack }) {
  const [{ users }] = useStateValue();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("standard");
  const [pin, setPin] = useState("");

  // Who's hosting. When the open house was scheduled with a host we just show
  // it; otherwise the agent MUST pick one before sign-ins can start, so a lead
  // is never created without an owner (Henry, 2026-09-06).
  const hostPreset = openHouseHasHost(openHouse);
  const [hostId, setHostId] = useState("");
  // The kiosk can be opened on a fresh device where the global users list is
  // empty; load it directly as a fallback so the picker is never blank.
  const [loadedUsers, setLoadedUsers] = useState([]);
  useEffect(() => {
    if (hostPreset) return;
    if (Array.isArray(users) && users.length) return;
    let live = true;
    getUsers().then((rows) => live && setLoadedUsers(rows || [])).catch(() => {});
    return () => { live = false; };
  }, [hostPreset, users]);

  const hosts = useMemo(
    () => eligibleHostingAgents(
      Array.isArray(users) && users.length ? users : loadedUsers,
    ),
    [users, loadedUsers],
  );

  useEffect(() => {
    if (!user?.id) return;
    const unsub = listenCheckinTemplates(user.id, (rows) => {
      setTemplates(rows);
      // Preselect the most recently used template, if any.
      const last = rows.slice().sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0))[0];
      if (last?.lastUsedAt) setTemplateId((cur) => (cur === "standard" ? last.id : cur));
    });
    return () => unsub && unsub();
  }, [user?.id]);

  const chosen =
    BUILT_IN_TEMPLATES.find((t) => t.id === templateId) ||
    templates.find((t) => t.id === templateId) ||
    DEFAULT_TEMPLATE;

  const chosenHost = hosts.find((h) => h.id === hostId);
  // A host is required: satisfied when the open house already names one, or the
  // agent picked one here.
  const hostReady = hostPreset || !!chosenHost;
  const canStart = /^\d{4}$/.test(pin) && hostReady;

  return (
    <div className="w-full max-w-md mx-auto text-white">
      <h1 className="font-serif text-3xl mb-1">Start Open House</h1>
      <p className="text-white/70 text-sm mb-6">
        {openHouse.propertyAddress?.split(",")[0]} · guests will see this device. Set a PIN to get back out.
      </p>

      {hostPreset ? (
        <div className="mb-5 rounded-lg bg-white/5 border border-white/10 p-3">
          <p className="text-xs uppercase tracking-widest text-[#d4af6a] mb-1">Hosting</p>
          <p className="text-white text-sm">
            {openHouse.hostingAgentName || "This open house's agent"}
            <span className="text-white/50"> · every sign-in becomes their lead</span>
          </p>
        </div>
      ) : (
        <div className="mb-5 rounded-lg bg-white/5 border border-[#d4af6a]/40 p-3">
          <label className="block text-xs uppercase tracking-widest text-[#d4af6a] mb-1">
            Who's hosting today?
          </label>
          <p className="text-white/50 text-xs mb-2">
            Every guest who signs in becomes this agent's lead.
          </p>
          <select
            value={hostId}
            onChange={(e) => setHostId(e.target.value)}
            className="w-full rounded-lg p-3 text-slate-900"
          >
            <option value="">Choose the hosting agent</option>
            {hosts.map((h) => (
              <option key={h.id} value={h.id}>{hostDisplayName(h)}</option>
            ))}
          </select>
        </div>
      )}

      <label className="block text-xs uppercase tracking-widest text-[#d4af6a] mb-2">Question set</label>
      <select
        value={templateId}
        onChange={(e) => setTemplateId(e.target.value)}
        className="w-full rounded-lg p-3 text-slate-900 mb-5"
      >
        {BUILT_IN_TEMPLATES.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
        {templates.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      <label className="block text-xs uppercase tracking-widest text-[#d4af6a] mb-2">4-digit exit PIN</label>
      <input
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
        inputMode="numeric"
        placeholder="0000"
        className="w-full rounded-lg p-3 text-slate-900 tracking-[0.5em] text-center text-xl mb-6"
      />

      <button
        onClick={() =>
          onStart({
            template: chosen,
            pin,
            host: chosenHost
              ? {
                  id: chosenHost.id,
                  name: hostDisplayName(chosenHost),
                  email: chosenHost.email || "",
                }
              : null,
          })
        }
        disabled={!canStart}
        className="w-full bg-[#d4af6a] disabled:opacity-40 text-[#1a2238] font-bold rounded-full py-4 text-lg"
      >
        Enter Guest Mode
      </button>
      {!hostReady && (
        <p className="text-white/40 text-xs text-center mt-3">
          Pick a host to begin — this is the one thing that can't be skipped.
        </p>
      )}
      <button onClick={onBack} className="w-full text-white/60 text-sm mt-4">
        Back to Open Houses
      </button>
    </div>
  );
}
